export const DEFAULT_MUSIC_SERVICE = 'spotify';

export const MUSIC_SERVICE_LABELS = {
  spotify: 'Spotify',
  'apple-music': 'Apple Music',
};

// Since we don't have per-service track IDs, links are search queries.
// Apple Music uses the 'us' storefront: the web player redirects signed-in
// users to their own storefront, and search works regardless for others.
const SEARCH_URL_BUILDERS = {
  spotify: (q) => `https://open.spotify.com/search/${encodeURIComponent(q)}`,
  'apple-music': (q) => `https://music.apple.com/us/search?term=${encodeURIComponent(q)}`,
};

/**
 * Build a music-service search URL for a track.
 * @param {Object} track - Track with name and artistName
 * @param {'spotify' | 'apple-music'} service
 */
export function buildMusicSearchUrl(track, service = DEFAULT_MUSIC_SERVICE) {
  const build = SEARCH_URL_BUILDERS[service] || SEARCH_URL_BUILDERS[DEFAULT_MUSIC_SERVICE];
  return build(`${track.name} ${track.artistName}`);
}

/**
 * Build a music-service search URL for an album.
 * @param {'spotify' | 'apple-music'} service
 */
export function buildAlbumSearchUrl(artistName, albumTitle, service = DEFAULT_MUSIC_SERVICE) {
  const build = SEARCH_URL_BUILDERS[service] || SEARCH_URL_BUILDERS[DEFAULT_MUSIC_SERVICE];
  return build(`${artistName} ${albumTitle}`);
}

/**
 * Slugify an artist name for use in URLs.
 * "Angels & Airwaves" → "angels-and-airwaves"
 */
export function slugifyArtist(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Un-slugify an artist slug back to a displayable name.
 * "angels-and-airwaves" → "Angels And Airwaves"
 */
export function unslugifyArtist(slug) {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build a Stellar artist page URL.
 * Used in share cards and share text.
 */
export function buildArtistPageUrl(artistName) {
  return `https://stellarmusic.io/artist/${slugifyArtist(artistName)}`;
}

/**
 * Format track links for clipboard copy. Prefers a track's precomputed
 * musicUrl (e.g. a canonical Apple Music link) over a search URL.
 * @param {Array} tracks - Array of track objects
 * @param {'links-only' | 'with-names'} format
 * @param {'spotify' | 'apple-music'} service
 * @returns {string}
 */
export function formatTrackLinks(tracks, format = 'with-names', service = DEFAULT_MUSIC_SERVICE) {
  return tracks
    .map((track, i) => {
      const url = track.musicUrl || buildMusicSearchUrl(track, service);
      if (format === 'with-names') {
        return `${i + 1}. ${track.name} — ${track.artistName}\n${url}`;
      }
      return url;
    })
    .join('\n');
}

/**
 * Generate a CSV string from track data.
 * @param {Array} tracks - Array of track objects
 * @param {'spotify' | 'apple-music'} service
 * @returns {string}
 */
export function generateCSV(tracks, service = DEFAULT_MUSIC_SERVICE) {
  const serviceLabel = MUSIC_SERVICE_LABELS[service] || MUSIC_SERVICE_LABELS[DEFAULT_MUSIC_SERVICE];
  const headers = [
    'Track Name',
    'Artist',
    'Album',
    'Duration (s)',
    'Deezer URL',
    `${serviceLabel} Search URL`,
    'Preview URL',
  ];

  const escapeCSV = (val) => {
    const str = String(val ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = tracks.map((t) => [
    t.name,
    t.artistName,
    t.albumName,
    Math.round((t.durationMs || 30000) / 1000),
    t.externalUrl || t.deezerUrl || '',
    t.musicUrl || buildMusicSearchUrl(t, service),
    t.previewUrl || '',
  ]);

  return [headers, ...rows].map((row) => row.map(escapeCSV).join(',')).join('\n');
}
